/**
 * DraggableItem Component
 *
 * A reusable wrapper component for drag-and-drop items using @dnd-kit/sortable.
 * Provides visual feedback during dragging and a grip handle.
 *
 * @example
 * ```tsx
 * <DraggableItem id="item-1" disabled={false}>
 *   <span>Item content</span>
 * </DraggableItem>
 * ```
 */

import React, { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

export interface DraggableItemProps {
  /** Unique identifier for the sortable item */
  id: string;
  /** Content to render inside the draggable wrapper */
  children: ReactNode;
  /** Whether dragging is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the grip handle */
  showHandle?: boolean;
}

/**
 * DraggableItem - Sortable wrapper for drag-and-drop items
 *
 * Uses @dnd-kit/sortable for smooth drag-and-drop functionality.
 * Includes a grip handle for initiating drag operations.
 */
export const DraggableItem: React.FC<DraggableItemProps> = ({
  id,
  children,
  disabled = false,
  className = '',
  showHandle = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded transition-all duration-150',
        isDragging && 'bg-dark-active shadow-lg ring-2 ring-brand-500/50',
        !disabled && 'hover:bg-dark-surface/50',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {showHandle && !disabled && (
        <button
          type="button"
          className="flex-shrink-0 p-1 text-dark-muted hover:text-dark-text cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical size={16} />
        </button>
      )}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

export default DraggableItem;
